<script setup>
import { ref, watch, onMounted } from "vue";
import { Icon } from "@iconify/vue";

const isOpen = ref(false);
const menu = ref();
const themes = ref([
  {
    value: "theme-light-blue",
    name: "Theme light bleu",
    bgColor: "bg-blue-100",
    textColor: "text-blue-800",
  },
  {
    value: "theme-dark-blue",
    name: "Theme dark bleu",
    bgColor: "bg-slate-800",
    textColor: "text-blue-100",
  },
  {
    value: "theme-light-gray",
    name: "Theme light gris",
    bgColor: "bg-slate-100",
    textColor: "text-slate-800",
  },
  {
    value: "theme-dark-gray",
    name: "Theme dark gris",
    bgColor: "bg-gray-800",
    textColor: "text-gray-100",
  },
  {
    value: "theme-light-pink",
    name: "Theme light pink",
    bgColor: "bg-rose-200",
    textColor: "text-rose-800",
  },
  {
    value: "theme-dark-pink",
    name: "Theme dark pink",
    bgColor: "bg-rose-800",
    textColor: "text-rose-200",
  },
  {
    value: "theme-light-green",
    name: "Theme light vert",
    bgColor: "bg-green-200",
    textColor: "text-green-800",
  },
  {
    value: "theme-dark-green",
    name: "Theme dark vert",
    bgColor: "bg-green-800",
    textColor: "text-green-200",
  },
  {
    value: "theme-dark-halloween",
    name: "Theme halloween",
    bgColor: "bg-black",
    textColor: "text-orange-600",
  },
  {
    value: "theme-christmas",
    name: "Theme de noel",
    bgColor: "bg-green-800",
    textColor: "text-red-600",
  },
]);

const themeClass = ref(localStorage.getItem("theme") || "theme-light");

// Traduction des variables de couleur
const colorLabels = {
  "color-primary": "Couleur principale",
  "color-secondary": "Couleur secondaire",
  "color-tertiary": "Couleur tertiaire",
  "color-validated": "Couleur de validation",
  "color-not-validated": "Couleur d’erreur",
  "color-text-primary": "Texte principal",
  "color-text-secondary": "Texte secondaire",
  "color-text-tertiary": "Texte tertiaire",
};

const showCustomTheme = ref(false);
const colorVars = [
  "color-primary",
  "color-secondary",
  "color-tertiary",
  "color-validated",
  "color-not-validated",
  "color-text-primary",
  "color-text-secondary",
  "color-text-tertiary",
];

const customColors = ref({
  "color-primary": "#fdfdfd",
  "color-secondary": "#deeaff",
  "color-tertiary": "#939fd8",
  "color-validated": "#b1f1bf",
  "color-not-validated": "#fee2e2",
  "color-text-primary": "#231f29",
  "color-text-secondary": "#4372cf",
  "color-text-tertiary": "#d6d5fd",
});

const openCustomTheme = () => {
  showCustomTheme.value = true;
};

const applyCustomTheme = () => {
  let css = ":root {";
  for (const key of colorVars) {
    css += `--${key}: ${customColors.value[key]};`;
  }
  css += "}";
  let styleTag = document.getElementById("custom-theme");
  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = "custom-theme";
    document.head.appendChild(styleTag);
  }
  styleTag.innerHTML = css;
  localStorage.setItem("custom-theme", JSON.stringify(customColors.value));
//   showCustomTheme.value = false;
  themeClass.value = ""; // Désactive les classes de thèmes prédéfinis
};

const resetCustomTheme = () => {
  let styleTag = document.getElementById("custom-theme");
  if (styleTag) styleTag.remove();
  localStorage.removeItem("custom-theme");
  themeClass.value = "theme-light";
  showCustomTheme.value = false;
};

const switchTheme = (theme) => {
  themeClass.value = theme;
  localStorage.setItem("theme", theme);
  let styleTag = document.getElementById("custom-theme");
  if (styleTag) styleTag.remove();
  localStorage.removeItem("custom-theme");
};

onMounted(() => {
  document.documentElement.className = themeClass.value;
  const saved = localStorage.getItem("custom-theme");
  if (saved) {
    customColors.value = JSON.parse(saved);
    applyCustomTheme();
  }
});

watch(themeClass, (newTheme) => {
  document.documentElement.className = newTheme;
});

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};
</script>

<template>
  <div :class="[themeClass, 'relative']">
    <div
      @click="toggleMenu"
      class="flex items-center p-1 space-x-3 duration-150 transform group hover:bg-secondary cursor-pointer"
    >
      <div class="p-2 duration-150 transform bg-primary rounded-xl">
        <Icon
          icon="solar:paint-roller-broken"
          size="16"
          class="duration-150 transform"
        />
      </div>
      <div class="flex flex-col">
        <span class="font-medium uppercase font-sulphurPoint">Mon thème</span>
        <span class="text-xs text-primaryT">bleu, gris, dark...</span>
      </div>
    </div>
    <transition name="nested">
      <div v-if="isOpen" ref="menu">
        <div class="bg-primary z-20 text-sm border-y border-primaryT">
          <div>
            <button
              v-for="(theme, index) in themes"
              :key="index"
              @click="switchTheme(theme.value)"
              :class="[
                `p-2 flex items-center space-x-1 group transform duration-150 w-full ${theme.bgColor} ${theme.textColor}`,
              ]"
            >
              <div class="flex flex-col">
                <p class="font-medium uppercase font-sulphurPoint">
                  {{ theme.name }}
                </p>
              </div>
            </button>
            <button
              @click="openCustomTheme"
              class="p-2 flex items-center space-x-1 w-full bg-white text-teal-700"
            >
              <span class="font-bold uppercase font-sulphurPoint"
                >Mon thème personnalisé 🎨</span
              >
            </button>
          </div>
          <div
            v-if="showCustomTheme"
            class="p-4 bg-primary border rounded"
          >
            <div
              v-for="(color, key) in customColors"
              :key="key"
              class="flex items-center mb-2"
            >
              <label
                class="w-48 capitalize font-medium font-sulphurPoint text-xs"
                >{{ colorLabels[key] }}</label
              >
              <div
                class="flex items-center justify-center rounded-full overflow-hidden h-8 w-8 border"
              >
                <input
                  type="color"
                  :id="key"
                  v-model="customColors[key]"
                  class="scale-200"
                />
              </div>
            </div>
            <div class="flex justify-end space-x-2">
              <button @click="resetCustomTheme" class="small-btn btn-outline">
                Réinitialiser
              </button>
              <button @click="applyCustomTheme" class="small-btn btn-primary">
                Appliquer
              </button>
            </div>
            <div class="flex justify-end mt-4">
              <button @click="showCustomTheme = false" class="small-btn">
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: all 0.15s ease-in-out;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(-100px);
  height: 0;
  opacity: 0;
}

.nested-sub-menu-enter-active,
.nested-sub-menu-leave-active {
  transition: all 0.15s ease-in-out;
}

.nested-sub-menu-enter-from,
.nested-sub-menu-leave-to {
  transform: translateX(-100px);
  opacity: 0;
}
</style>
